import { useState, useCallback, useRef, useMemo, useEffect } from 'react'
import { Send, Loader2, Paperclip, X, FileText, Smile } from 'lucide-react'
import { toast } from 'sonner'
import { Button } from '@/components/ui/Button'
import { cn } from '@/lib/utils'
import { EMOJIS } from '@/lib/constants'
import {
  ALLOWED_FILE_TYPES,
  ALLOWED_FILE_TYPES_DISPLAY,
  isAllowedFileType,
  isFileSizeAllowed,
  formatFileSize,
  MAX_FILE_SIZE,
  extractPastedFile
} from '@/lib/fileUpload'
import type { useFileUpload } from '@/hooks/useFileUpload'

type FileUploadHook = ReturnType<typeof useFileUpload>

interface SimpleMessageComposerProps {
  value: string
  onChange: (value: string) => void
  onSubmit: (e: React.FormEvent) => void
  isSending: boolean
  fileUpload: FileUploadHook
  placeholder?: string
  autoFocus?: boolean
}

export function SimpleMessageComposer({
  value,
  onChange,
  onSubmit,
  isSending,
  fileUpload,
  placeholder = 'Type your message...',
  autoFocus = false
}: SimpleMessageComposerProps) {
  const [showEmojiPicker, setShowEmojiPicker] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const { pendingFile, handleFileSelect, handleFileRemove } = fileUpload

  // Create object URL for image preview
  const previewUrl = useMemo(() => {
    if (pendingFile?.file.type.startsWith('image/')) {
      return URL.createObjectURL(pendingFile.file)
    }
    return null
  }, [pendingFile?.file])

  useEffect(() => {
    return () => {
      if (previewUrl) URL.revokeObjectURL(previewUrl)
    }
  }, [previewUrl])

  const handlePaste = useCallback((e: React.ClipboardEvent) => {
    if (pendingFile) return
    const file = extractPastedFile(e.clipboardData)
    if (!file) return

    e.preventDefault()

    if (!isAllowedFileType(file.type)) {
      toast.error('File type not supported', {
        description: `Allowed: ${ALLOWED_FILE_TYPES_DISPLAY}`
      })
      return
    }

    if (!isFileSizeAllowed(file.size)) {
      toast.error('File too large', {
        description: `Maximum file size is ${formatFileSize(MAX_FILE_SIZE)}`
      })
      return
    }

    handleFileSelect(file)
  }, [pendingFile, handleFileSelect])

  const handleFileChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return

    e.target.value = ''

    if (!isAllowedFileType(file.type)) {
      toast.error('File type not supported', {
        description: `Allowed: ${ALLOWED_FILE_TYPES_DISPLAY}`
      })
      return
    }

    if (!isFileSizeAllowed(file.size)) {
      toast.error('File too large', {
        description: `Maximum file size is ${formatFileSize(MAX_FILE_SIZE)}`
      })
      return
    }

    handleFileSelect(file)
  }, [handleFileSelect])

  const insertEmoji = useCallback((emoji: string) => {
    onChange(value + emoji)
    setShowEmojiPicker(false)
  }, [value, onChange])

  const canSubmit = (value.trim() || pendingFile) && !isSending && pendingFile?.status !== 'uploading'

  return (
    <div className="border-t border-border bg-card p-4 shrink-0">
      <div className="space-y-2">
        {/* Pending file preview */}
        {pendingFile && (
          <div className="flex items-center gap-2 p-2 bg-muted rounded-lg">
            {previewUrl ? (
              <img src={previewUrl} alt={pendingFile.file.name} className="w-10 h-10 object-cover rounded" />
            ) : (
              <FileText className="size-10 text-muted-foreground" />
            )}
            <div className="flex-1 min-w-0">
              <div className="text-sm font-medium truncate">{pendingFile.file.name}</div>
              <div className="text-xs text-muted-foreground">
                {pendingFile.status === 'uploading' ? `Uploading ${pendingFile.progress}%` :
                 pendingFile.status === 'error' ? pendingFile.error :
                 formatFileSize(pendingFile.file.size)}
              </div>
              {pendingFile.status === 'uploading' && (
                <div className="h-1 bg-muted-foreground/20 rounded mt-1 overflow-hidden">
                  <div
                    className="h-full bg-primary transition-[width]"
                    style={{ width: `${pendingFile.progress}%` }}
                  />
                </div>
              )}
            </div>
            {pendingFile.status !== 'uploading' && (
              <button
                type="button"
                onClick={handleFileRemove}
                className="p-1 hover:bg-background rounded cursor-pointer"
              >
                <X className="size-4 text-muted-foreground" />
              </button>
            )}
          </div>
        )}

        {/* Emoji picker */}
        {showEmojiPicker && (
          <div className="flex flex-wrap gap-1 p-2 bg-muted rounded-lg">
            {EMOJIS.map(emoji => (
              <button
                key={emoji}
                type="button"
                onClick={() => insertEmoji(emoji)}
                className="size-9 text-xl hover:bg-background rounded cursor-pointer flex items-center justify-center"
              >
                {emoji}
              </button>
            ))}
          </div>
        )}

        <form onSubmit={onSubmit} className="flex gap-2 items-end">
          {/* Hidden file input */}
          <input
            ref={fileInputRef}
            type="file"
            className="hidden"
            accept={ALLOWED_FILE_TYPES.join(',')}
            onChange={handleFileChange}
          />

          {/* Attachment button */}
          <Button
            type="button"
            variant="ghost"
            size="icon"
            onClick={() => fileInputRef.current?.click()}
            disabled={isSending || !!pendingFile}
            className="cursor-pointer shrink-0"
            title="Attach file"
          >
            <Paperclip className="size-4" />
          </Button>

          {/* Emoji button */}
          <Button
            type="button"
            variant="ghost"
            size="icon"
            onClick={() => setShowEmojiPicker(!showEmojiPicker)}
            disabled={isSending}
            className={cn("cursor-pointer shrink-0", showEmojiPicker && "bg-muted")}
            title="Insert emoji"
          >
            <Smile className="size-4" />
          </Button>

          <textarea
            value={value}
            onChange={(e) => onChange(e.target.value)}
            onPaste={handlePaste}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault()
                if (canSubmit) {
                  onSubmit(e)
                }
              }
            }}
            placeholder={placeholder}
            className="flex-1 bg-muted rounded-lg px-4 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring resize-none min-h-[40px] max-h-[120px]"
            disabled={isSending}
            rows={1}
            style={{ height: 'auto', overflow: 'hidden' }}
            onInput={(e) => {
              const target = e.target as HTMLTextAreaElement
              target.style.height = 'auto'
              target.style.height = `${Math.min(target.scrollHeight, 120)}px`
            }}
            autoFocus={autoFocus}
          />

          <Button
            type="submit"
            disabled={!canSubmit}
            className="cursor-pointer shrink-0"
          >
            {pendingFile?.status === 'uploading' ? (
              <Loader2 className="size-4 animate-spin" />
            ) : (
              <Send className="size-4" />
            )}
          </Button>
        </form>
      </div>
    </div>
  )
}
